/// <reference types="node" />
import * as React from 'react'
import {
  Button,
  Create,
  FileField,
  FileInput,
  SimpleForm,
  Toolbar,
  useDataProvider,
  useGetIdentity,
  useNotify,
  useRefresh,
  SelectInput,
} from 'react-admin'
import { useMutation } from 'react-query'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
} from '@mui/material'
// @ts-ignore
import Papa from 'papaparse'

// Define the form field type
interface FormField {
  id: string
  label: string
  source: string
  required: boolean
}

// Define the form section type
interface FormSection {
  id: string
  title: string
  fields: FormField[]
}

// Define the mapping type
interface HeaderMapping {
  csvHeader: string
  formField: string | null
}

// Define the import status type
interface ImportStatus {
  import_log_status?: string
  import_log_total?: string
  import_log_current?: string
  organisation_status?: string
  organisation_total?: string
  organisation_current?: string
  processed_state?: number
  processed_message?: string
}

const ImportToolbar = ({ onCancel, importLogId, ...props }) => (
  <Toolbar {...props}>
    {importLogId === null ? (
      <Button
        onClick={onCancel}
        variant="contained"
        color="secondary"
        sx={{
          padding: '8px 24px',
          fontWeight: 'bold',
          boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
          '&:hover': {
            boxShadow: '0 5px 8px rgba(0,0,0,0.3)',
          },
        }}
      >
        <span>Cancel</span>
      </Button>
    ) : (
      <Button
        onClick={onCancel}
        variant="contained"
        color="primary"
        sx={{
          padding: '8px 24px',
          fontWeight: 'bold',
          boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
          '&:hover': {
            boxShadow: '0 5px 8px rgba(0,0,0,0.3)',
          },
        }}
      >
        <span>Close</span>
      </Button>
    )}
  </Toolbar>
)

const VendorImport = (props) => {
  const [loading, setLoading] = React.useState(false)
  const [csvFile, setCsvFile] = React.useState<File | null>(null)
  const [csvHeaders, setCsvHeaders] = React.useState<string[]>([])
  const [csvPreview, setCsvPreview] = React.useState<any[]>([])
  const [formSections, setFormSections] = React.useState<FormSection[]>([])
  const [headerMappings, setHeaderMappings] = React.useState<HeaderMapping[]>(
    []
  )
  const [activeStep, setActiveStep] = React.useState(0)
  const [importLogId, setImportLogId] = React.useState<number | null>(null)
  const [importStatus, setImportStatus] = React.useState<ImportStatus | null>(
    null
  )
  const [statusPolling, setStatusPolling] = React.useState<ReturnType<
    typeof setInterval
  > | null>(null)
  const [consecutiveErrors, setConsecutiveErrors] = React.useState<number>(0)

  const dataProvider = useDataProvider()
  const notify = useNotify()
  const refresh = useRefresh()
  const identity = useGetIdentity()

  // Load the organisation's vendor form configuration
  const fetchedRef = React.useRef(false)

  // Store the current organisation ID in a ref to detect changes
  const orgIdRef = React.useRef(null)

  // Extract organisation ID to a stable reference
  const organisationId = identity.identity?.organisation?.id

  React.useEffect(() => {
    // Update the ref with the current organisation ID
    orgIdRef.current = organisationId

    const loadVendorForm = async () => {
      // Skip if we've already fetched the data and the org ID hasn't changed
      if (fetchedRef.current && orgIdRef.current === organisationId) {
        console.log('Skipping duplicate vendor form fetch')
        return
      }

      // Update the stored org ID
      orgIdRef.current = organisationId

      try {
        console.log('Loading vendor form...')
        console.log('Organisation ID:', organisationId)
        if (!organisationId) {
          console.log('No organisation ID, skipping fetch')
          return
        }

        const endpoint = organisationId
          ? `vendor-forms/organisations/${organisationId}`
          : 'organisations/current/vendor-forms'
        const response = await dataProvider.custom(endpoint, {
          method: 'GET',
        })
        console.log('Vendor form response:', response)

        // Check if we got a list of vendor forms or a single vendor form
        if (Array.isArray(response.data)) {
          // We got a list of vendor forms, use the first one if available
          if (response.data.length > 0) {
            console.log(
              'Setting form sections from first vendor form in list:',
              response.data[0].vendor_form
            )
            setFormSections(response.data[0].vendor_form || [])
          } else {
            console.log(
              'No vendor forms found in list, setting empty form sections'
            )
            setFormSections([])
          }
        } else if (response.data?.vendor_form) {
          // We got a single vendor form
          console.log(
            'Setting form sections from single vendor form:',
            response.data.vendor_form
          )
          setFormSections(response.data.vendor_form)
        } else {
          console.log('No vendor form found, setting empty form sections')
          setFormSections([])
        }

        // Mark as fetched after successful fetch
        fetchedRef.current = true
      } catch (error) {
        console.error('Error loading vendor form:', error)
        notify('Error loading vendor form configuration', { type: 'error' })
        setFormSections([])
      }
    }

    if (organisationId) {
      console.log('Identity loaded, calling loadVendorForm')
      loadVendorForm()
    } else {
      console.log('No organisation ID found in identity')
    }

    // Reset the ref when the component unmounts
    return () => {
      fetchedRef.current = false
    }
  }, [dataProvider, notify, organisationId])

  // Add cleanup for the polling interval
  React.useEffect(() => {
    return () => {
      if (statusPolling) {
        clearInterval(statusPolling)
      }
    }
  }, [statusPolling])

  // Helper function to check if a string is an acronym of another string
  const isAcronym = (possibleAcronym: string, fullText: string): boolean => {
    // Clean up the strings - remove non-alphabetic characters and split into words
    const cleanFullText = fullText.replace(/[^a-z\s]/g, '').split(/\s+/)
    const cleanAcronym = possibleAcronym.replace(/[^a-z]/g, '')

    // If the possible acronym is too long, it's not an acronym
    if (cleanAcronym.length > cleanFullText.length) {
      return false
    }

    // Check if the acronym matches the first letters of words in the full text
    // First, try to match in sequence
    const firstLetters = cleanFullText.map((word) => word.charAt(0)).join('')
    if (firstLetters.includes(cleanAcronym)) {
      return true
    }

    // If that fails, check if all letters in the acronym appear in the right order in the full text
    let fullTextIndex = 0
    let acronymIndex = 0

    while (
      fullTextIndex < fullText.length &&
      acronymIndex < cleanAcronym.length
    ) {
      if (
        fullText[fullTextIndex].toLowerCase() === cleanAcronym[acronymIndex]
      ) {
        acronymIndex += 1
      }
      fullTextIndex += 1
    }

    return acronymIndex === cleanAcronym.length
  }

  // Parse file to extract headers and preview data
  const parseFile = (file: File) => {
    console.log('parseFile called with file:', file)
    setLoading(true)

    // Check if file is Excel or CSV based on extension
    const fileName = file.name.toLowerCase()
    const isExcel = fileName.endsWith('.xls') || fileName.endsWith('.xlsx')

    if (isExcel) {
      // For Excel files, we need to upload to the server to get a preview
      console.log('Processing Excel file')

      // Set the file
      setCsvFile(file)

      // Show loading message
      notify('Uploading Excel file for preview...', { type: 'info' })

      // Upload the file to get a preview
      dataProvider
        .uploadFile('vendors/preview-excel', {
          import_file: { rawFile: file },
          organisation_id: identity.identity?.organisation?.id,
        })
        .then((response) => {
          console.log('Excel preview response:', response)

          // Extract headers and preview data
          const headers = response.data?.headers || []
          const previewData = response.data?.preview || []

          // Set the headers and preview data
          setCsvHeaders(headers)
          setCsvPreview(previewData)

          // Create initial mappings (default to null)
          const initialMappings = headers.map((header) => ({
            csvHeader: header,
            formField: null,
          }))

          // Try to automatically map headers to form fields based on similar names
          let autoMappings = initialMappings

          // Only attempt auto-mapping if there are form sections defined
          if (formSections && formSections.length > 0) {
            console.log('Form sections available, attempting auto-mapping')
            // Keep track of already assigned form fields to prevent duplicates
            const usedFormFields = new Set()

            autoMappings = headers.map((header) => {
              let bestMatch: string | null = null
              let bestMatchScore = 0

              // Flatten all form fields from all sections
              const allFields = formSections.flatMap((section) =>
                section.fields.map((field) => ({
                  id: field.id,
                  label: field.label,
                  source: field.source,
                  section: section.title, // Add section title for acronym matching
                }))
              )

              // Find the best match based on string similarity
              allFields.forEach((field) => {
                // Skip if this form field has already been assigned
                if (usedFormFields.has(field.source)) {
                  return
                }

                const headerLower = header.toLowerCase()
                const labelLower = field.label.toLowerCase()
                const sectionLower = field.section.toLowerCase()

                let score = 0

                // Exact match
                if (headerLower === labelLower) {
                  score = 1
                }
                // Partial match - header contains label or label contains header
                else if (
                  headerLower.includes(labelLower) ||
                  labelLower.includes(headerLower)
                ) {
                  score = 0.8
                }
                // Acronym match - check if header is an acronym of section title
                // e.g., "SPO" for "Service Provider Owner Details"
                else if (isAcronym(headerLower, sectionLower)) {
                  score = 0.9
                }
                // Check for specific field types like "Email Address"
                else if (
                  headerLower.includes('email') &&
                  labelLower.includes('email')
                ) {
                  // For email fields, ensure we're matching the right context
                  // by checking if other parts of the header match the section
                  const headerParts = headerLower.split(/\s+/)
                  const sectionParts = sectionLower.split(/\s+/)

                  // Check if any non-email parts of the header match parts of the section
                  const nonEmailParts = headerParts.filter(
                    (part) => part !== 'email' && part !== 'address'
                  )
                  const matchingParts = nonEmailParts.some((part) =>
                    sectionParts.some(
                      (secPart) =>
                        secPart.includes(part) || part.includes(secPart)
                    )
                  )

                  if (matchingParts) {
                    score = 0.85
                  } else {
                    score = 0.6
                  }
                }

                if (score > bestMatchScore) {
                  bestMatch = field.source
                  bestMatchScore = score
                }
              })

              // If we found a match, add it to the used fields set
              if (bestMatch) {
                usedFormFields.add(bestMatch)
              }

              return {
                csvHeader: header,
                formField: bestMatch,
              }
            })
          }

          console.log('Auto mappings:', autoMappings)
          setHeaderMappings(autoMappings)

          // Only automatically advance to step 1 if form sections are available
          if (formSections && formSections.length > 0) {
            console.log('Form sections available, setting activeStep to 1')
            setActiveStep(1) // Move to mapping step
            console.log('ActiveStep should now be 1')
          } else {
            console.log('No form sections available, staying on step 0')
            console.log('User will need to click Next button manually')
          }

          setLoading(false)
        })
        .catch((error) => {
          console.error('Error getting Excel preview:', error)
          notify(
            `Error getting Excel preview: ${error.message || 'Unknown error'}`,
            { type: 'error' }
          )

          // Set some placeholder data for the preview
          const placeholderHeaders = ['Error loading Excel preview']
          setCsvHeaders(placeholderHeaders)
          setCsvPreview([
            {
              'Error loading Excel preview':
                'Please try again or use a CSV file instead',
            },
          ])

          // Create empty mappings
          const initialMappings = placeholderHeaders.map((header) => ({
            csvHeader: header,
            formField: null,
          }))
          setHeaderMappings(initialMappings)

          setLoading(false)
        })
    } else {
      // For CSV files, use Papa.parse
      console.log('Processing CSV file')
      Papa.parse(file, {
        header: true,
        preview: 5, // Parse only first 5 rows for preview
        complete: (results) => {
          console.log('Papa.parse complete with results:', results)
          if (results.data && results.data.length > 0) {
            // Extract headers from the first row
            const headers = results.meta.fields || []
            console.log('Extracted headers:', headers)
            setCsvHeaders(headers)

            // Create initial mappings (default to null)
            const initialMappings = headers.map((header) => ({
              csvHeader: header,
              formField: null,
            }))

            // Try to automatically map headers to form fields based on similar names
            let autoMappings = initialMappings

            // Only attempt auto-mapping if there are form sections defined
            if (formSections && formSections.length > 0) {
              console.log('Form sections available, attempting auto-mapping')
              // Keep track of already assigned form fields to prevent duplicates
              const usedFormFields = new Set()

              autoMappings = headers.map((header) => {
                let bestMatch: string | null = null
                let bestMatchScore = 0

                // Flatten all form fields from all sections
                const allFields = formSections.flatMap((section) =>
                  section.fields.map((field) => ({
                    id: field.id,
                    label: field.label,
                    source: field.source,
                    section: section.title, // Add section title for acronym matching
                  }))
                )
                console.log('Form sections:', formSections)
                console.log('All fields for mapping:', allFields)

                // Find the best match based on string similarity
                allFields.forEach((field) => {
                  // Skip if this form field has already been assigned
                  if (usedFormFields.has(field.source)) {
                    return
                  }

                  const headerLower = header.toLowerCase()
                  const labelLower = field.label.toLowerCase()
                  const sectionLower = field.section.toLowerCase()

                  let score = 0

                  // Exact match
                  if (headerLower === labelLower) {
                    score = 1
                  }
                  // Partial match - header contains label or label contains header
                  else if (
                    headerLower.includes(labelLower) ||
                    labelLower.includes(headerLower)
                  ) {
                    score = 0.8
                  }
                  // Acronym match - check if header is an acronym of section title
                  // e.g., "SPO" for "Service Provider Owner Details"
                  else if (isAcronym(headerLower, sectionLower)) {
                    score = 0.9
                  }
                  // Check for specific field types like "Email Address"
                  else if (
                    headerLower.includes('email') &&
                    labelLower.includes('email')
                  ) {
                    // For email fields, ensure we're matching the right context
                    // by checking if other parts of the header match the section
                    const headerParts = headerLower.split(/\s+/)
                    const sectionParts = sectionLower.split(/\s+/)

                    // Check if any non-email parts of the header match parts of the section
                    const nonEmailParts = headerParts.filter(
                      (part) => part !== 'email' && part !== 'address'
                    )
                    const matchingParts = nonEmailParts.some((part) =>
                      sectionParts.some(
                        (secPart) =>
                          secPart.includes(part) || part.includes(secPart)
                      )
                    )

                    if (matchingParts) {
                      score = 0.85
                    } else {
                      score = 0.6
                    }
                  }

                  if (score > bestMatchScore) {
                    bestMatch = field.source
                    bestMatchScore = score
                  }
                })

                // If we found a match, add it to the used fields set
                if (bestMatch) {
                  usedFormFields.add(bestMatch)
                }

                return {
                  csvHeader: header,
                  formField: bestMatch,
                }
              })
            } else {
              console.log('No form sections available, using initial mappings')
            }

            console.log('Auto mappings:', autoMappings)
            setHeaderMappings(autoMappings)
            setCsvPreview(results.data)

            // Always set the csvFile state to ensure the Next button is visible
            setCsvFile(file)

            // Only automatically advance to step 1 if form sections are available
            if (formSections && formSections.length > 0) {
              console.log('Form sections available, setting activeStep to 1')
              setActiveStep(1) // Move to mapping step
              console.log('ActiveStep should now be 1')
            } else {
              console.log('No form sections available, staying on step 0')
              console.log('User will need to click Next button manually')
            }
          } else {
            console.log('CSV file appears to be empty')
            notify('The CSV file appears to be empty', { type: 'warning' })
          }
          setLoading(false)
        },
        error: (error) => {
          console.error('Error parsing CSV:', error)
          notify(`Error parsing CSV: ${error.message}`, { type: 'error' })
          setLoading(false)
        },
      })
    }
  }

  // Handle file selection
  const handleFileChange = (files) => {
    console.log('handleFileChange called with:', files)

    // Handle case where files is a single file object with rawFile property
    if (files && files.rawFile) {
      const file = files.rawFile
      console.log('File selected (from files.rawFile):', file)
      setCsvFile(file)
      parseFile(file)
    }
    // Handle case where files is an array of file objects
    else if (
      files &&
      Array.isArray(files) &&
      files.length > 0 &&
      files[0].rawFile
    ) {
      const file = files[0].rawFile
      console.log('File selected (from files[0].rawFile):', file)
      setCsvFile(file)
      parseFile(file)
    }
    // Handle case where files is a File object directly
    else if (files instanceof File) {
      console.log('File selected (direct File object):', files)
      setCsvFile(files)
      parseFile(files)
    } else {
      console.log(
        'No files selected or file object is in an unexpected format:',
        files
      )
    }
  }

  // Update a specific header mapping
  const updateMapping = (csvHeader: string, formField: string | null) => {
    setHeaderMappings(
      headerMappings.map((mapping) =>
        mapping.csvHeader === csvHeader ? { ...mapping, formField } : mapping
      )
    )
  }

  // Get all available form fields as options for the select input
  const getFormFieldOptions = () => {
    const options: Array<{ id: string; name: string }> = []

    // Add a "Do not import" option
    options.push({ id: 'do_not_import', name: 'Do not import' })

    // Add all form fields from all sections if they exist
    if (formSections && formSections.length > 0) {
      console.log('Adding form fields to options')
      formSections.forEach((section) => {
        section.fields.forEach((field) => {
          options.push({
            id: field.source,
            name: `${section.title} - ${field.label}`,
          })
        })
      })
    } else {
      console.log('No form sections available for options')
      // Add a message if no form fields are available
      options.push({
        id: 'no_fields',
        name: 'No form fields available - please configure vendor form first',
      })
    }

    console.log('Form field options:', options)
    return options
  }

  const { mutate } = useMutation<any, Error, any>(
    ['upload', 'importFile', {}],
    (values) => dataProvider.uploadFile('vendors/upload-with-tags', values),
    {
      onSuccess: (res: any) => {
        // Store the import log ID
        const logId = res.id
        console.log('Import log ID from response:', logId)
        setImportLogId(logId)

        // Start polling for status updates
        const polling = setInterval(() => {
          console.log('Polling for import status with ID:', logId)
          dataProvider
            .getImportStatus(logId)
            .then(({ data }) => {
              console.log('Import status data:', data)
              // Reset consecutive errors counter on successful response
              setConsecutiveErrors(0)
              console.log('Setting import status with data:', data.status)
              setImportStatus(data.status)

              // If the import is completed or failed, stop polling
              if (
                (data.status?.import_log_status &&
                  data.status.import_log_status.toString() === 'COMPLETED') ||
                data.processed_state === 1 ||
                data.processed_state === 2
              ) {
                console.log('Import completed or failed, stopping polling')
                clearInterval(polling)
                setStatusPolling(null)

                // Show completion notification
                if (data.processed_state === 1) {
                  notify(
                    `Import completed: ${
                      data.processed_message || 'Successfully'
                    }`,
                    { type: 'success' }
                  )
                } else if (data.processed_state === 2) {
                  notify(
                    `Import failed: ${
                      data.processed_message || 'Unknown error'
                    }`,
                    { type: 'warning' }
                  )
                } else if (
                  data.status?.import_log_status &&
                  data.status.import_log_status.toString() === 'COMPLETED'
                ) {
                  notify('Import completed successfully', { type: 'success' })
                }

                // Refresh the data
                refresh()

                // Close the drawer after completion
                if (props.closeDrawer) {
                  props.closeDrawer()
                }
              }
            })
            .catch((error) => {
              console.error('Error fetching import status:', error)

              // Increment consecutive errors counter
              setConsecutiveErrors((prev) => prev + 1)

              // If we've had too many consecutive errors, show a message and stop polling
              if (consecutiveErrors > 3) {
                clearInterval(polling)
                setStatusPolling(null)
                notify(
                  `Error checking import status: ${
                    error.message || 'Unknown error'
                  }. The import may still be processing in the background.`,
                  { type: 'warning' }
                )
              }
            })
        }, 2000) // Check every 2 seconds

        setStatusPolling(polling)

        // Show initial notification
        notify(res.message || 'Vendors import started', {
          type: 'info',
        })

        // Don't close the drawer immediately
        // if (props.closeDrawer) {
        //   props.closeDrawer()
        // }
      },
      onError: (e: any) => {
        // Check if we have structured error data (from a 422 response)
        if (e.data) {
          console.error('Validation error details:', e.data)

          // Format the error message based on the structure of e.data
          let errorMessage = 'Import validation error: '

          // Handle array of validation errors
          if (Array.isArray(e.data)) {
            errorMessage += e.data
              .map((err) => {
                // Each validation error might have loc, msg, type
                const location = err.loc ? err.loc.join(' > ') : ''
                return `${location ? `${location}: ` : ''}${
                  err.msg || JSON.stringify(err)
                }`
              })
              .join('; ')
          }
          // Handle object with error fields
          else if (typeof e.data === 'object') {
            const errorDetails = Object.entries(e.data)
              .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
              .join('; ')
            errorMessage += errorDetails || JSON.stringify(e.data)
          }
          // Fallback for any other format
          else {
            errorMessage += JSON.stringify(e.data)
          }

          notify(errorMessage, { type: 'warning' })
        } else {
          // Fallback to simple error message
          notify(`Import error: ${e.message}`, { type: 'warning' })
        }
      },
    }
  )

  const importFile = (values) => {
    if (!csvFile) {
      notify('Please select a CSV file', { type: 'warning' })
      return
    }

    // Check if there are form sections available
    if (!formSections || formSections.length === 0) {
      notify('Please configure the vendor form before importing vendors', {
        type: 'warning',
      })
      return
    }

    // Check if there's a field mapped to the "name" tag category
    const hasNameField = headerMappings.some(
      (mapping) =>
        mapping.formField &&
        (mapping.formField.toLowerCase().includes('name') ||
          formSections.some((section) =>
            section.fields.some(
              (field) =>
                field.source === mapping.formField &&
                field.label.toLowerCase().includes('name')
            )
          ))
    )

    if (!hasNameField) {
      notify('You must map a field to the "name" tag category', {
        type: 'warning',
      })
      return
    }

    // Create an object with the file and mappings
    const data = {
      import_file: { rawFile: csvFile },
      organisation_id: identity.identity?.organisation?.id,
      header_mappings: JSON.stringify(headerMappings),
    }

    console.log('Submitting import with data:', data)

    // Don't close the drawer immediately, let the user see the progress
    // if (props.closeDrawer) {
    //   props.closeDrawer()
    // }

    mutate(data)
  }

  // Go back to file selection step
  const handleBack = () => {
    setActiveStep(0)
    setCsvHeaders([])
    setCsvPreview([])
    setHeaderMappings([])
  }

  // Proceed to confirmation step
  const handleNext = () => {
    setActiveStep(2)
  }

  if (identity.isLoading) {
    return null
  }

  return (
    <Create {...props} sx={{ width: '90vw', maxWidth: '1200px' }}>
      <SimpleForm
        toolbar={
          <ImportToolbar
            onCancel={() => props.closeDrawer && props.closeDrawer()}
            loading={loading}
            importLogId={importLogId}
          />
        }
        onSubmit={importFile}
        sx={{ width: '100%', maxWidth: '100%', overflowX: 'visible' }}
      >
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          <Step>
            <StepLabel>Select CSV File</StepLabel>
          </Step>
          <Step>
            <StepLabel>Map Headers</StepLabel>
          </Step>
          <Step>
            <StepLabel>Confirm Import</StepLabel>
          </Step>
        </Stepper>

        {activeStep === 0 && (
          <Box sx={{ width: '100%' }}>
            <Typography variant="body1" paragraph>
              Upload a CSV or Excel file with vendor data. You&apos;ll be able
              to map the columns to your vendor form fields in the next step.
            </Typography>
            {loading === true && <CircularProgress />}
            <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'visible' }}>
              <FileInput
                source="import_file"
                label="CSV or Excel file"
                accept=".csv,.xls,.xlsx,text/csv,application/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={handleFileChange}
                sx={{ width: '100%', maxWidth: '100%' }}
              >
                <FileField source="src" title="title" />
              </FileInput>
            </Box>

            {csvFile && (
              <Box display="flex" justifyContent="flex-end" mt={3}>
                <Button
                  label="Next"
                  onClick={() => setActiveStep(1)}
                  variant="contained"
                  color="primary"
                  sx={{
                    padding: '8px 24px',
                    fontWeight: 'bold',
                    boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                    '&:hover': {
                      boxShadow: '0 5px 8px rgba(0,0,0,0.3)',
                    },
                  }}
                />
              </Box>
            )}
          </Box>
        )}

        {activeStep === 1 && (
          <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'visible' }}>
            <Typography variant="h6" gutterBottom>
              Map CSV Headers to Form Fields
            </Typography>
            <Typography variant="body2" paragraph>
              For each column in your CSV file, select the corresponding field
              in your vendor form. Select &quot;Do not import&quot; for columns
              you don&apos;t want to import.
            </Typography>
            <Typography
              variant="body2"
              paragraph
              sx={{ color: 'error.main', fontWeight: 'bold' }}
            >
              Note: You must map at least one field to a field containing
              &quot;name&quot; in its label. This field will be used as the
              vendor name.
            </Typography>

            {(!formSections || formSections.length === 0) && (
              <Paper sx={{ p: 2, mb: 3, bgcolor: 'warning.light' }}>
                <Typography variant="body1" color="warning.dark">
                  <strong>Warning:</strong> No vendor form fields have been
                  configured. Please configure the vendor form in the
                  Organisation settings before importing vendors.
                </Typography>
                <Typography variant="body2" color="warning.dark" sx={{ mt: 1 }}>
                  You can still map headers, but you won&apos;t be able to do
                  the import until the vendor form is configured.
                </Typography>
              </Paper>
            )}

            <TableContainer component={Paper} sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>CSV Header</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Form Field</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {headerMappings.map((mapping) => (
                    <TableRow key={mapping.csvHeader}>
                      <TableCell>{mapping.csvHeader}</TableCell>
                      <TableCell>
                        <SelectInput
                          source={`mapping_${mapping.csvHeader}`}
                          choices={getFormFieldOptions()}
                          optionText="name"
                          optionValue="id"
                          onChange={(e) =>
                            updateMapping(
                              mapping.csvHeader,
                              e.target.value === 'do_not_import' ||
                                e.target.value === 'no_fields'
                                ? null
                                : e.target.value
                            )
                          }
                          defaultValue={mapping.formField || 'do_not_import'}
                          fullWidth
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="h6" gutterBottom>
              CSV Preview
            </Typography>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {csvHeaders.map((header) => (
                      <TableCell key={header}>{header}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {csvPreview.map((row, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {csvHeaders.map((header) => (
                        <TableCell key={`${rowIndex}-${header}`}>
                          {row[header]}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box display="flex" justifyContent="space-between" mt={3}>
              <Button
                label="Back"
                onClick={handleBack}
                variant="contained"
                color="secondary"
                sx={{
                  padding: '8px 24px',
                  fontWeight: 'bold',
                  boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                  '&:hover': {
                    boxShadow: '0 5px 8px rgba(0,0,0,0.3)',
                  },
                }}
              />
              <Button
                label="Next"
                onClick={handleNext}
                disabled={!formSections || formSections.length === 0}
                title={
                  !formSections || formSections.length === 0
                    ? 'Please configure the vendor form before proceeding'
                    : ''
                }
                variant="contained"
                color="primary"
                sx={{
                  padding: '8px 24px',
                  fontWeight: 'bold',
                  boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                  '&:hover': {
                    boxShadow: '0 5px 8px rgba(0,0,0,0.3)',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#cccccc',
                    color: '#666666',
                  },
                }}
              />
            </Box>
          </Box>
        )}

        {activeStep === 2 && (
          <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'visible' }}>
            <Typography variant="h6" gutterBottom>
              Confirm Import
            </Typography>
            <Typography variant="body1" paragraph>
              You&apos;re about to import vendors from your CSV file with the
              mappings you&apos;ve defined. Click &quot;Import&quot; to proceed
              import.
            </Typography>

            <Typography variant="subtitle1" gutterBottom>
              Mapping Summary:
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>CSV Header</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Form Field</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {headerMappings.map((mapping) => {
                    // Find the form field label for display
                    let fieldLabel = 'Do not import'
                    if (mapping.formField) {
                      for (const section of formSections) {
                        for (const field of section.fields) {
                          if (field.source === mapping.formField) {
                            fieldLabel = `${section.title} - ${field.label}`
                            break
                          }
                        }
                      }
                    }

                    return (
                      <TableRow key={mapping.csvHeader}>
                        <TableCell>{mapping.csvHeader}</TableCell>
                        <TableCell>{fieldLabel}</TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Button
                label="Back"
                onClick={() => setActiveStep(1)}
                variant="contained"
                color="secondary"
                disabled={importLogId !== null}
                sx={{
                  padding: '8px 24px',
                  fontWeight: 'bold',
                  boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                  '&:hover': {
                    boxShadow: '0 5px 8px rgba(0,0,0,0.3)',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#cccccc',
                    color: '#666666',
                  },
                }}
              />

              <Box
                display="flex"
                alignItems="center"
                gap={2}
                sx={{ flexGrow: 1, justifyContent: 'flex-end' }}
              >
                {importLogId && importStatus && (
                  <Box
                    display="flex"
                    alignItems="center"
                    sx={{ mr: 2, maxWidth: '60%' }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ mr: 1, whiteSpace: 'nowrap' }}
                    >
                      <span style={{ fontWeight: 'bold' }}>Status:</span>
                      <span
                        style={{
                          color:
                            importStatus.import_log_status === 'COMPLETED'
                              ? 'green'
                              : '#1a90ff',
                          marginLeft: '4px',
                        }}
                      >
                        {importStatus.import_log_status ?? 'Processing...'}
                      </span>{' '}
                      <span style={{ fontWeight: 'bold', marginLeft: '8px' }}>
                        Progress:
                      </span>{' '}
                      <span style={{ color: '#1a90ff' }}>
                        {importStatus.import_log_current ?? '0'}
                      </span>{' '}
                      /{' '}
                      <span style={{ color: '#1a90ff' }}>
                        {importStatus.import_log_total ?? '0'}
                      </span>
                    </Typography>
                    <Box sx={{ width: '120px', ml: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={
                          (Number(importStatus.import_log_current ?? '0') /
                            Math.max(
                              Number(importStatus.import_log_total ?? '1'),
                              1
                            )) *
                          100
                        }
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: '#e0e0e0',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 4,
                            backgroundColor: '#1a90ff',
                          },
                        }}
                      />
                    </Box>
                  </Box>
                )}
                {importLogId && !importStatus && (
                  <CircularProgress size={20} sx={{ mr: 2 }} />
                )}

                <Button
                  label="Import"
                  onClick={importFile}
                  variant="contained"
                  color="primary"
                  disabled={loading || importLogId !== null}
                  sx={{
                    opacity: loading || importLogId !== null ? 0.6 : 1,
                    '&.Mui-disabled': {
                      backgroundColor: '#cccccc',
                      color: '#666666',
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>
        )}
      </SimpleForm>
    </Create>
  )
}

export default VendorImport
